import { TransformPosition, TransformRotation } from '../interfaces';
import CameraManager from '../bridge/engine/CameraManager';
import { CameraConfiguration } from '../bridge/configuration/interfaces/CameraConfiguration';
import { Vector3, Animation, CircleEase, ArcRotateCamera } from '@babylonjs/core';

const DEFAULT_ANGLES = [
	45 * (Math.PI / 180),
	135 * (Math.PI / 180),
	225 * (Math.PI / 180),
	315 * (Math.PI / 180),
];

// TODO improve
export interface CameraSettings {
	horizontalLimits: number | { min: number; max: number } | null;
	verticalLimits: number | { min: number; max: number } | null;
	transitionTime: number;
	layerMask?: number;
}

export interface Keys {
	frame: number;
	value: number;
}

// TODO better isolation from Babylon? no?
// WIP!
export class Camera {
	private focusBoundings: { min: Vector3; max: Vector3 } | null;
	private settings: CameraSettings;
	constructor(private manager: CameraManager, public readonly id: string) {
		this.settings = {
			horizontalLimits: null,
			verticalLimits: null,
			transitionTime: 300,
		};
	}

	public setPosition(position: TransformPosition): void {
		const currentPosition = this.manager.getCamera(this.id).position;

		const newPosition = new Vector3(
			position.x || currentPosition.x || 0,
			position.y || currentPosition.y || 0,
			position.z || currentPosition.z || 0
		);

		this.manager.configurateCamera({ position: newPosition }, this.id);
	}

	public getPosition(): TransformPosition {
		const { x, y, z } = this.manager.getCamera(this.id).position;
		return {
			x,
			y,
			z,
		};
	}

	public setRotation(rotation: TransformRotation): void {
		const currentAlpha = this.manager.getCamera(this.id).alpha;
		const currentBeta = this.manager.getCamera(this.id).beta;

		const newAlpha = rotation.y || currentAlpha || 0;
		const newBeta = rotation.x || currentBeta || 0;

		this.manager.configurateCamera({ alpha: newAlpha, beta: newBeta }, this.id);
	}

	public setVerticalRotation(rotation: number): void {
		const currentAlpha = this.manager.getCamera(this.id).alpha;
		const newAlpha = rotation || currentAlpha || 0;
		this.manager.configurateCamera({ alpha: newAlpha }, this.id);
	}

	public rotateVertically(value: number, onAnimationEnd?: () => void): void {
		this.stopAnimation('verticalRotation');
		const camera = this.manager.getCamera(this.id);
		const currentAlpha = camera.alpha;

		const rad360 = 360 * (Math.PI / 180);
		const currentRotationModular = currentAlpha % rad360;
		const currentDefault = DEFAULT_ANGLES.reduce((prev, curr) => {
			return Math.abs(curr - currentRotationModular) < Math.abs(prev - currentRotationModular)
				? curr
				: prev;
		});
		const newRotation = currentDefault + value;

		const keys: Keys[] = [];
		keys.push({
			frame: 0,
			value: currentRotationModular,
		});
		keys.push({
			frame: 10,
			value: newRotation,
		});

		const animation = new Animation(
			'verticalRotation',
			'alpha',
			30,
			Animation.ANIMATIONTYPE_FLOAT,
			Animation.ANIMATIONLOOPMODE_CONSTANT
		);
		const easingFunction = new CircleEase();
		easingFunction.setEasingMode(1);
		animation.setKeys(keys);
		animation.setEasingFunction(easingFunction);
		camera.animations = [animation];
		this.manager.beginAnimation([animation], 0, 10, onAnimationEnd, this.id);
	}

	public stopAnimation(name: string): void {
		this.manager.stopAnimation(name, this.id);
	}

	public focusOn(
		boundingExtends: { min: TransformPosition; max: TransformPosition },
		mobile = false,
		forceUpdate = false
	): void {
		const update = this.updateFocusBoundings(boundingExtends);
		if (!update && !forceUpdate) {
			return;
		}

		if (this.focusBoundings) {
			const config = new Object() as CameraConfiguration;

			if (this.settings.transitionTime || this.settings.transitionTime == 0) {
				config.framingTime = this.settings.transitionTime;
				config.elevationReturnTime = this.settings.transitionTime;
			}

			this.manager.configurateCamera(config, this.id);

			this.manager.autoFocus(
				{
					min: this.focusBoundings.min,
					max: this.focusBoundings.max,
				},
				mobile,
				this.id
			);
		}
	}

	public reset(props: Array<string> = ['target', 'alpha', 'beta', 'radius']): void {
		this.manager.resetDefault(props, this.id);
		this.focusBoundings = null;
	}

	public screenshot(callback: (data: string) => void, mobile?: boolean): void {
		this.manager.createScreenShot(callback, mobile, this.id);
	}

	public setHorizontalLimits(value: number | { min: number; max: number } | null): void {
		this.settings.horizontalLimits = value;
		if (!value && value !== 0) {
			this.manager.configurateCamera({ lowerAlphaLimit: null, upperAlphaLimit: null }, this.id);
			return;
		}
		const min = this.getMinLimit('horizontalLimits');
		const max = this.getMaxLimit('horizontalLimits');

		const lowerAlphaLimit = this.manager.getCamera(this.id).alpha - min;
		const upperAlphaLimit = this.manager.getCamera(this.id).alpha + max;
		this.manager.configurateCamera({ lowerAlphaLimit, upperAlphaLimit }, this.id);
	}

	public setVerticalLimits(value: number | { min: number; max: number }): void {
		this.settings.verticalLimits = value;
		if (!value && value !== 0) {
			this.manager.configurateCamera({ lowerBetaLimit: null, upperBetaLimit: null }, this.id);
			return;
		}
		const min = this.getMinLimit('verticalLimits');
		const max = this.getMaxLimit('verticalLimits');

		const lowerBetaLimit = this.manager.getCamera(this.id).beta - min;
		const upperBetaLimit = this.manager.getCamera(this.id).beta + max;
		this.manager.configurateCamera({ lowerBetaLimit, upperBetaLimit }, this.id);
	}

	public setTransitionTime(value: number): void {
		this.settings.transitionTime = value;
	}

	public setLayerMask(value: number): void {
		this.manager.configurateCamera({ layerMask: value }, this.id);
	}

	public getCamera(id: string): ArcRotateCamera {
		return this.manager.getCamera(id);
	}

	private updateFocusBoundings(boundingExtends: {
		min: TransformPosition;
		max: TransformPosition;
	}): boolean {
		let { min, max } = boundingExtends;
		min = this.serializePosition(min);
		max = this.serializePosition(max);
		const minVector = new Vector3(min.x, min.y, min.z);
		const maxVector = new Vector3(max.x, max.y, max.z);
		if (this.focusBoundings) {
			const limit = 0.05;
			if (
				!minVector.equalsWithEpsilon(this.focusBoundings.min, limit) ||
				!maxVector.equalsWithEpsilon(this.focusBoundings.max, limit)
			) {
				this.focusBoundings = {
					min: minVector,
					max: maxVector,
				};
				return true;
			} else {
				return false;
			}
		}
		this.focusBoundings = {
			min: minVector,
			max: maxVector,
		};
		return true;
	}

	private serializePosition(position: TransformPosition): { x: number; y: number; z: number } {
		return Object.assign({ x: 0, y: 0, z: 0 }, position) as { x: number; y: number; z: number };
	}

	private getMinLimit(type: string): number {
		return typeof this.settings[type] == 'number' ? this.settings[type] : this.settings[type].min;
	}

	private getMaxLimit(type: string): number {
		return typeof this.settings[type] == 'number' ? this.settings[type] : this.settings[type].max;
	}
}
